<script setup>
import { reactive, ref, inject } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useElementSize } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

import NewBaseNotification from '@dogfy/components/base/NewBaseNotification.vue'
import BaseButton from '@dogfy/components/base/BaseButton.vue'
import BaseField from '@dogfy/components/base/BaseField.vue'
import BaseFieldInput from '@dogfy/components/base/BaseFieldInput.vue'
import LoginAvatarDodi from '@/components/LoginAvatarDodi.vue'
import SocialLogin from '@/components/SocialLogin.vue'

import { email, required } from '@dogfy/lib/i18n-validators'
import { errorMessage, messageType, resetValidations } from '@dogfy/lib/vuelidateHelpers'
import config from '@/config'

import { useVuelidate } from '@vuelidate/core'

import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()

const toast = inject('toast')

const { t, locale: currentLocale } = useI18n()

const authStore = useAuthStore()

const signUpUrl = `${import.meta.env.VUE_APP_WEBSITE_URL}${config.dogfyLinks.signUp.path[currentLocale.value]}`

const state = reactive({
  isLoading: false,
  isTypingEmail: false,
  isTypingPassword: false,
})

const credentials = reactive({
  email: null,
  password: null,
})

const emailInputRef = ref(null)
const { width: emailInputWidth } = useElementSize(emailInputRef)

const validationRules = {
  email: { required, email },
  password: { required },
}

const v$ = useVuelidate(validationRules, credentials, { $scope: false })

async function login () {
  try {
    state.isLoading = true

    await v$.value.$validate()
    if (v$.value.$error) return

    await authStore.login(credentials)

    await router.push({ path: route.query.redirect || '/' })
  } catch (err) {
    err.response?.status === 403
      ? toast.error(t('auth.error.userIsCancelled'))
      : toast.error(t('auth.error.couldNotLogin'))
    console.error(err)
  } finally {
    state.isLoading = false
  }
}
</script>

<template>
  <div class="login-view grid-layout container-layout">
    <div class="login-view__content">
      <LoginAvatarDodi
        role="presentation"
        :email="credentials.email"
        :email-input-width="emailInputWidth"
        :is-typing-email="state.isTypingEmail"
        :is-typing-password="state.isTypingPassword"
      />
      <form class="login-view__form" @submit.prevent="login">
        <fieldset class="login-view__fieldset">
          <legend class="login-view__legend">
            {{ $t('authLoginView.label.login') }}
          </legend>
          <BaseField
            :message="errorMessage(v$, 'email')"
            :message-type="messageType(v$, 'email')"
            is-fullwidth
          >
            <BaseFieldInput
              ref="emailInputRef"
              v-model.trim.lowercase="credentials.email"
              type="email"
              autocomplete="email"
              icon="mail"
              :placeholder="$t('auth.placeholder.email')"
              :aria-label="$t('common.label.email')"
              data-test-id="inputEmail"
              @focus="state.isTypingEmail = true"
              @blur="state.isTypingEmail = false"
              @update:model-value="resetValidations(v$, 'email')"
            />
          </BaseField>
          <BaseField
            :message="errorMessage(v$, 'password')"
            :message-type="messageType(v$, 'password')"
            is-fullwidth
          >
            <BaseFieldInput
              v-model="credentials.password"
              type="password"
              autocomplete="password"
              :placeholder="$t('auth.placeholder.password')"
              has-password-reveal
              is-fullwidth
              :aria-label="$t('common.label.password')"
              data-test-id="inputPassword"
              @focus="state.isTypingPassword = true"
              @blur="state.isTypingPassword = false"
              @update:model-value="resetValidations(v$, 'password')"
            />
          </BaseField>
        </fieldset>
        <div class="buttonset buttonset--column buttonset--half-gap">
          <BaseButton
            class="login-view__button"
            native-type="submit"
            :is-loading="state.isLoading"
            has-loading-overlay
            data-test-id="btnNext"
          >
            {{ $t('common.cta.continue') }}
          </BaseButton>
          <BaseButton
            type="link-color small"
            tag="router-link"
            :to="{ name: 'resetPassword' }"
            data-test-id="btnResetPassword"
          >
            {{ $t('authLoginView.cta.recoverPassword') }}
          </BaseButton>
        </div>
      </form>
    </div>
    <SocialLogin class="login-view__social-login" />
    <NewBaseNotification
      class="login-view__notification"
      :actions="[
        {
          label: $t('authLoginView.cta.signUp'),
          tag: 'a',
          href: signUpUrl,
          target: '_blank',
        },
      ]"
    >
      <span v-html="$t('authLoginView.message.stillNotHavingAccount')" />
    </NewBaseNotification>
  </div>
</template>

<style lang="scss">
.login-view {
  row-gap: $spacer*1.5;

  padding-top: 4rem;
  padding-bottom: 1.5rem;
  height: 100%;

  &__content {
    display: flex;

    flex-direction: column;
    gap: $spacer*1.5;
    align-items: center;

    width: 100%;

    @include grid-column(1, 2, 5);
    @include grid-colspan(4);

    @include breakpoint(lg) {
      gap: $spacer;
    }
  }

  &__form {
    display: flex;

    flex-direction: column;
    gap: $spacer*1.5;
    align-items: stretch;

    width: 100%;

    @include breakpoint(lg) {
      gap: $spacer;
    }
  }

  &__fieldset {
    display: flex;

    flex-direction: column;
    gap: $spacer;
  }

  &__legend {
    margin-bottom: $spacer;

    font-size: ms(2, true);
    line-height: $font-lineheight-large;
    font-weight: $font-weight-semibold;
    text-align: center;
  }

  &__button {
    align-self: center;

    width: 100%;

    @include breakpoint(lg) {
      max-width: 343px;
    }
  }

  &__social-login {
    @include grid-column(1, 2, 4);
    @include grid-colspan(4, 4, 6);
  }

  &__notification {
    @include grid-column(1, 1, 3);
    @include grid-colspan(4, 6, 8);
  }
}
</style>
